import React, { useEffect, useState, useCallback } from "react";
import { auth, db } from "../FirebaseConfig";
import BankSelector from "../component/BankSelector";
import {
  doc,
  getDoc,
  addDoc,
  serverTimestamp,
  getDocs,
  collection,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { toast } from "react-toastify"; // For notifications

const BuyerProfile = () => {
  const user = auth.currentUser;
  const navigate = useNavigate(); // Initialize useNavigate
  const [profileData, setProfileData] = useState({
    name: "",
    gender: "",
    profilePic: "",
    city: "",
    role: "buyer", // Default role
    bankName: "", // New: Bank Name
    accountNumber: "", // New: Account Number
    accountName: "", // New: Account Name
  });
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [buyerOrders, setBuyerOrders] = useState([]);
  const [nigerianBanks, setNigerianBanks] = useState([]); // State for banks

  // Function to fetch orders (memoized with useCallback)
  const fetchBuyerOrders = useCallback(async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, "Orders"),
        where("buyerId", "==", user.uid)
      );
      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBuyerOrders(orders);
    } catch (error) {
      console.error("Error fetching buyer orders:", error);
    }
  }, [user]);

  // Fetch Nigerian banks
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const response = await fetch("https://nigerianbanks.xyz/"); // A public API for Nigerian banks
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setNigerianBanks(data);
      } catch (error) {
        console.error("Error fetching Nigerian banks:", error);
        toast.error("Failed to fetch bank list.");
      }
    };

    fetchBanks();
  }, []);

  useEffect(() => {
    fetchBuyerOrders();
  }, [fetchBuyerOrders]);

  // Payout logic - now more integrated with markItemAsReceived
  // const triggerPayout = useCallback(
  //   async (orderId) => {
  //     try {
  //       const res = await fetch("/api/payout", {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({ orderId }),
  //       });

  //       if (res.ok) {
  //         // Update Firestore: set payoutDone to true for the order
  //         const orderRef = doc(db, "Orders", orderId);
  //         await updateDoc(orderRef, { payoutDone: true });
  //         console.log("Payout successful for order:", orderId);
  //         // Refresh orders to reflect payoutDone status
  //         fetchBuyerOrders();
  //       } else {
  //         const errorData = await res.json();
  //         console.error(
  //           `Payout failed for order ${orderId}:`,
  //           errorData.error || "Unknown error"
  //         );
  //         alert(
  //           `Payout failed for order ${orderId}: ${
  //             errorData.error || "Please try again later."
  //           }`
  //         );
  //       }
  //     } catch (err) {
  //       console.error(`Payout request error for order ${orderId}:`, err);
  //       alert("An error occurred while attempting payout. Check console for details.");
  //     }
  //   },
  //   [fetchBuyerOrders]
  // );

  const markItemAsReceived = async (orderId, itemId) => {
    try {
      setBuyerOrders((prevOrders) => {
        const orderToUpdate = prevOrders.find((order) => order.id === orderId);
  
        if (!orderToUpdate) {
          console.error("Order not found in admin panel state:", orderId);
          return prevOrders;
        }
  
        const updatedItems = orderToUpdate.items.map((item) =>
          item.id === itemId ? { ...item, delivered: true } : item
        );
  
        const allItemsDelivered = updatedItems.every((item) => item.delivered === true);
  
        const updatedOrder = {
          ...orderToUpdate,
          items: updatedItems,
          received: allItemsDelivered,
        };
  
        // Removed triggerPayout(orderId)
  
        return prevOrders.map((order) => (order.id === orderId ? updatedOrder : order));
      });
  
      toast.success("Item marked as delivered!");
    } catch (err) {
      console.error("Failed to mark item as delivered:", err);
      toast.error("An error occurred while marking the item.");
    }
  };
  

  const shouldRemind = (timestamp) => {
    if (!timestamp?.seconds) return false;
    const orderDate = new Date(timestamp.seconds * 1000);
    const now = new Date();
    const diffInDays = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));
    return diffInDays >= 3;
  };

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      const docRef = doc(db, "profiles", user.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setProfileData(docSnap.data());
      }
      setLoading(false);
    };

    fetchData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", "unsigned_ibuyunn");

    try {
      toast.info("Uploading image...");
      const res = await fetch("https://api.cloudinary.com/v1_1/dm8uirujp/upload", {
        method: "POST",
        body: data,
      });

      if (!res.ok) {
        throw new Error(`Cloudinary upload failed: ${res.statusText}`);
      }
      const result = await res.json();
      setProfileData((prev) => ({ ...prev, profilePic: result.secure_url }));
      toast.success("Profile picture updated!");
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload image. Please try again.");
    }
  };

  const handleSave = async () => {
    if (!user) {
      toast.error("User not logged in.");
      return;
    }

    const docRef = doc(db, "profiles", user.uid);
    try {
      // Validate bank details if the role is being set to "Seller"
      if (profileData.role === "seller") {
        if (!profileData.bankName || profileData.bankName.trim() === "") {
          toast.error("Please select your bank.");
          return;
        }
        if (!profileData.accountNumber || profileData.accountNumber.trim() === "") {
          toast.error("Please enter your account number.");
          return;
        }
        if (!profileData.accountName || profileData.accountName.trim() === "") {
          toast.error("Please enter your account name.");
          return;
        }

        // Safely split bank info (bank name | bank code) if it comes from the dropdown
        let bankNameToStore = profileData.bankName;
        let bankCodeToStore = "";
        if (profileData.bankName.includes('|')) {
          [bankNameToStore, bankCodeToStore] = profileData.bankName.split('|');
        }

        await updateDoc(docRef, {
          ...profileData,
          bankName: bankNameToStore,
          bankCode: bankCodeToStore, // Store bank code as well
        });
        toast.success("Profile updated and role set to Seller!");
        navigate('/seller'); // Navigate to seller dashboard
      } else {
        // If not a seller, ensure bank details are cleared or not set
        const { bankName, accountNumber, accountName, ...dataToSave } = profileData;
        await updateDoc(docRef, dataToSave);
        toast.success("Profile updated successfully!");
      }

      setEditMode(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile. Please try again.");
    }
  };

  if (loading)
    return <p className="text-white text-center mt-10 animate-pulse">Loading Profile...</p>;

  return (
    <div className="min-h-screen bg-transparent text-white py-12 px-6 flex flex-col items-center justify-center">
      <div className="bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl p-10 w-full max-w-4xl transition-all duration-300">
        <div className="flex flex-col md:flex-row items-center gap-10">
          {/* Profile Image */}
          <div className="relative text-center">
            <img
              src={profileData.profilePic || "/default-profile.png"}
              alt="Profile"
              className="w-40 h-40 object-cover rounded-full border-4 border-white/30 shadow-md hover:scale-105 transition-transform duration-300"
            />
            {editMode && (
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="mt-4 text-sm text-gray-200"
              />
            )}
          </div>

          {/* Profile Details */}
          <div className="flex-1 w-full space-y-6">
            <ProfileField
              label="Full Name"
              name="name"
              value={profileData.name}
              editMode={editMode}
              onChange={handleChange}
              placeholder="Enter full name"
            />

            <ProfileField
              label="Gender"
              name="gender"
              value={profileData.gender}
              editMode={editMode}
              onChange={handleChange}
              isSelect
              options={[
                { value: "", label: "Select Gender" },
                { value: "male", label: "Male" },
                { value: "female", label: "Female" },
              ]}
            />

            {/* Role selection dropdown */}
            <ProfileField
              label="Role"
              name="role"
              value={profileData.role}
              editMode={editMode} // Allow editing only in edit mode
              onChange={handleChange}
              isSelect
              options={[
                { value: "buyer", label: "buyer" },
                { value: "seller", label: "seller" },
              ]}
            />

            {/* Conditionally rendered bank details for sellers */}
            {profileData.role === "seller" && (
              <>
                <h3 className="text-xl font-bold text-white mt-8 mb-4">
                  Seller Bank Details
                </h3>
                <ProfileField
                  label="Bank Name"
                  name="bankName"
                  value={profileData.bankName}
                  editMode={editMode}
                  onChange={handleChange}
                  isSelect
                  options={[
                    { value: "", label: "Select Your Bank" },
                    ...nigerianBanks.map((bank) => ({
                      value: `${bank.name}|${bank.code}`, // Store name and code
                      label: bank.name,
                    })),
                  ]}
                />
                <ProfileField
                  label="Account Number"
                  name="accountNumber"
                  value={profileData.accountNumber}
                  editMode={editMode}
                  onChange={handleChange}
                  placeholder="Enter Account Number"
                />
                <ProfileField
                  label="Account Name"
                  name="accountName"
                  value={profileData.accountName}
                  editMode={editMode}
                  onChange={handleChange}
                  placeholder="Enter Account Name"
                />
              </>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-10 flex justify-end gap-4">
          {editMode ? (
            <>
              <button
                onClick={() => setEditMode(false)}
                className="bg-gray-700 hover:bg-gray-800 text-white px-5 py-2 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg shadow transition"
              >
                Save
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg transition"
            >
              Edit Profile
            </button>
          )}
        </div>

        {success && (
          <p className="text-green-400 text-center mt-5 font-medium">
            Profile updated successfully!
          </p>
        )}
      </div>

      <div className="mt-16 w-full max-w-4xl">
        <h2 className="text-2xl font-bold mb-6">Your Orders</h2>
        {buyerOrders.length === 0 ? (
          <p className="text-gray-300">You have not ordered any products yet.</p>
        ) : (
          <div className="space-y-6">
            {buyerOrders.map((order) => (
              <div
                key={order.id}
                className="bg-white/10 p-5 rounded-xl shadow-md border border-white/20"
              >
                <p className="text-lg font-semibold text-green-400 mb-3">
                  Order ID: {order.id}
                </p>
                <p className="text-gray-300">
                  Order Date: {order.createdAt?.toDate().toLocaleString() || "N/A"}
                </p>
                <p className="mb-4">
                  <strong>Overall Order Status:</strong>{" "}
                  {order.received ? "All Items Received ✅" : "Pending Item(s) Reception ❌"}
                  {order.payoutDone && <span className="ml-2 text-blue-400">(Payout Processed)</span>}
                </p>

                <h3 className="text-xl font-semibold mb-3">Items in this Order:</h3>
                {order.items && order.items.length > 0 ? (
                  <ul className="space-y-3">
                    {order.items.map((item, itemIndex) => (
                      <li
                        key={item.id || itemIndex}
                        className="bg-white/5 p-4 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center"
                      >
                        <div>
                          <p className="font-medium text-white">
                            {item.name} (x{item.quantity}) - ₦{item.price.toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-400">
                            Seller: {item.sellerEmail || "N/A"}
                          </p>
                          <p className="text-sm text-gray-400">
                            Pickup Station: {item.DeliveryAddress || "check your email"}
                          </p>
                          <p className="text-sm text-gray-400">
                            Seller Phone: {item.PhoneNumber || "check your email"}
                          </p>
                          <p className="mt-1">
                            <strong>Item Status:</strong>{" "}
                            {item.delivered ? "Received ✅" : "Pending ❌"}
                          </p>
                        </div>
                        {!item.delivered && (
                          <button
                            onClick={() => markItemAsReceived(order.id, item.id)}
                            className="mt-3 sm:mt-0 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition"
                          >
                            Mark Item as Received
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-300">No items found for this order.</p>
                )}

                {/* Overall order reminder logic */}
                {!order.received && shouldRemind(order.createdAt) && (
                  <p className="text-yellow-400 mt-4 text-sm">
                    ⚠️ Don’t forget to mark ALL items in this order as received!
                  </p>
                )}
                {order.received && !order.payoutDone && (
                  <p className="text-blue-400 mt-4 text-sm">
                    Payout is being processed for this order.
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable ProfileField component (unchanged)
const ProfileField = ({
  label,
  name,
  value,
  onChange,
  editMode,
  placeholder,
  isSelect = false,
  options = [],
}) => (
  <div className="border border-white/20 rounded-xl px-4 text-xl py-3 bg-white/5 backdrop-blur-sm">
    <label className="text-xs text-gray-400 uppercase tracking-wide">{label}</label>
    {editMode ? (
      isSelect ? (
        <select
          name={name}
          value={value}
          onChange={onChange}
          className="w-full mt-1 bg-transparent border-none text-white px-1 py-1 focus:outline-none"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="text-black">
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full mt-1 bg-transparent border-none text-white px-1 py-1 focus:outline-none"
        />
      )
    ) : (
      <p className="text-white mt-1 font-medium">{value || "Not provided"}</p>
    )}
  </div>
);

export default BuyerProfile;